import React, { useState } from 'react';

import useModal from '../hooks/useModal';
import { ManualBooking, OffDuty, Reschedule } from '@/components/dashboard';
import { Request } from '@/data/Requests';

interface CalendarModalsProps {
  // Date props (provided by withSelectedDate)
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;

  // Modal control props
  modals: {
    manualBooking: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    offDuty: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };

  // Reschedule modal props
  setRequestToReschedule: (request: Request | null) => void;
}

interface CalendarModalsInputProps {
  // Date props (provided by withSelectedDate)
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
}

/**
 * NOTE: This HOC is designed to be composed with the withSelectedDate HOC,
 * as it relies on selectedDate and setSelectedDate props being present.
 */

/**
 * Higher-Order Component that provides all calendar-related modals with their state management.
 *
 * This HOC wraps a component and provides:
 * - Calendar-specific state (selectedDate, requestToReschedule)
 * - Modal state and controls as props
 * - Renders all modal components
 *
 * @param WrappedComponent - The component to wrap with modal functionality
 * @returns Enhanced component with modal props and calendar state
 */
const withCalendarModals = <P extends CalendarModalsProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const ComponentWithCalendarModals = (props: CalendarModalsInputProps) => {
    // Reschedule state
    const [requestToReschedule, setRequestToReschedule] =
      useState<Request | null>(null);

    // Modal states
    const manualBookingModal = useModal();
    const offDutyModal = useModal();

    // Modal controls object
    const modals = {
      manualBooking: {
        isOpen: manualBookingModal.isOpen,
        onOpen: manualBookingModal.onOpen,
        onClose: manualBookingModal.onClose
      },
      offDuty: {
        isOpen: offDutyModal.isOpen,
        onOpen: offDutyModal.onOpen,
        onClose: offDutyModal.onClose
      }
    };

    const onCloseRescheduleModal = () => {
      setRequestToReschedule(null);
    };

    return (
      <>
        <WrappedComponent
          {...(props as P)}
          selectedDate={props.selectedDate}
          setSelectedDate={props.setSelectedDate}
          modals={modals}
          setRequestToReschedule={setRequestToReschedule}
        />

        {/* Modal Components */}
        <ManualBooking
          isOpen={manualBookingModal.isOpen}
          onClose={manualBookingModal.onClose}
          selectedDate={props.selectedDate}
          presentingElement={undefined}
          setSelectedDate={props.setSelectedDate}
        />
        <Reschedule
          isOpen={!!requestToReschedule}
          requestToReschedule={requestToReschedule}
          onClose={onCloseRescheduleModal}
          presentingElement={undefined}
          setSelectedDate={props.setSelectedDate}
        />
        <OffDuty
          isOpen={offDutyModal.isOpen}
          onClose={offDutyModal.onClose}
          selectedDate={props.selectedDate}
          presentingElement={undefined}
        />
      </>
    );
  };

  ComponentWithCalendarModals.displayName = `withCalendarModals(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithCalendarModals;
};

export default withCalendarModals;
